import React, { useContext, useEffect, useState } from 'react';
import ExitToAppIcon from '@mui/icons-material/ExitToApp';
import packageJson from '../../../package.json';

import {
  Drawer,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider,
  Typography,
  Tooltip,
  Box,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import { Link } from 'react-router-dom';
import {
  AccountBalance,
  CheckBoxOutlined,
  EmailOutlined,
  HelpOutline,
  HomeOutlined,
  Label,
  MenuBookOutlined,
  Payment,
  PollOutlined,
  SettingsOutlined,
  ShareOutlined,
} from '@mui/icons-material';
import { PartnerContext, UserContext } from '../../lib/contexts';
import { makeStyles } from '@mui/styles';
import { Logo } from '../Logo';
import cn from 'classnames';
import { toCapitalizeFromUnderScore, toUnderScoreSlug } from '../../lib/helpers/helpers';

const drawerWidth = 250;
const drawerWidthSlim = 250;

const useStyles = makeStyles((theme) => ({
  root: {
    display: 'flex',
  },
  active: {
    backgroundColor: '#fff',
    color: theme.palette.admin.contrastText,
  },
  drawer: {
    width: theme.breakpoints.up('xl') ? drawerWidth : drawerWidthSlim,
    flexShrink: 0,
    '@media print': {
      display: 'none',
    },
  },
  drawerClosed: {
    width: theme.breakpoints.up('xl') ? drawerWidth : drawerWidthSlim,
    marginLeft: -1 * (theme.breakpoints.up('xl') ? drawerWidth : drawerWidthSlim),
    flexShrink: 0,
    '@media print': {
      display: 'none',
    },
  },
  drawerPaper: {
    width: 'inherit',
    backgroundColor: theme.palette.admin.dark,
    color: theme.palette.admin.contrastText,
    '& *': {
      color: theme.palette.admin.contrastText,
      fontWeight: 'bold',
    },
  },
  drawerText: {
    color: theme.palette.admin.contrastText,
    padding: 0,
  },
  toolbar: theme.mixins.toolbar,
  topBox: {
    height: 87,
    backgroundColor: theme.palette.admin.main,
  },
  partnerName: {
    margin: '20px auto 20px',
    padding: 0,
    fontWeight: 'bold',
    fontSize: 14,
  },
  listItem: {
    height: 55,
  },
  listItemActive: {},
  listItemText: {
    height: 35,
    display: 'block',
    margin: 'auto',
    padding: '0px',
    fontWeight: 'bold',
    '& *': {
      verticalAlign: '-webkit-baseline-middle',
      color: theme.palette.admin.contrastText + ' !important',
    },
  },
  ListItemIcon: {
    verticalAlign: '-webkit-baseline-middle',
    marginRight: 0,
    marginTop: 0,
    height: 35,
  },
  sideNavList: {
    width: '100%',
    padding: 0,
    margin: 0,
    '& a': {
      display: 'contents',
    },
    '& :hover': {
      backgroundColor: theme.palette.admin.backgroundHover + ' !important',
    },
  },
  comingSoonBadge: {
    width: 100,
    fontSize: '9px !important',
    position: 'relative',
    borderRadius: 10,
    padding: 4,
    margin: 0,
    top: -20,
    right: -90,
    backgroundColor: theme.palette.admin.backgroundHover + ' !important',
  },
  internalInfo: {
    marginLeft: 15,
    marginBottom: 0,
    fontSize: 11,
    color: theme.palette.admin.light,
  },
  internalInfoRole: {
    marginLeft: 15,
    marginBottom: -10,
    fontSize: 11,
    color: theme.palette.admin.light,
  },
}));

const APISideBarNavigation = () => {
  const {
    state: { code: partnerCode, name: partnerName, userRole },
  } = useContext(PartnerContext);
  const {
    actions: { logout },
  } = useContext(UserContext);
  const classes = useStyles();
  //const [open, setOpen] = useState(false);
  const [showShareLink, setShowShareLink] = useState(false);
  const [isOpen, setIsOpen] = useState(true);
  const active = '';
  const theme = useTheme();
  const matchSM = useMediaQuery(theme.breakpoints.up('sm'));
  const matchMD = useMediaQuery(theme.breakpoints.up('md'));
  const matchLG = useMediaQuery(theme.breakpoints.up('lg'));
  const matchXL = useMediaQuery(theme.breakpoints.up('xl'));

  useEffect(() => {
    setIsOpen(matchSM || matchMD);
  }, [matchSM, matchMD]);

  const page_access = [
    { name: 'Getting Started' },
    { name: 'Retrieving Results' },
    { name: 'Choose Integration' },
    { name: 'Customize Integration' },
    { name: 'API Endpoints' },
    //{ name: 'Typings' },
    { name: 'Schemas' },
    //{ name: 'Example Client', href:'https://api-prod.dietid.com/api/v1/docs/example/api_client' },
    {
      name: 'Data Dictionary',
      href: 'https://www.dropbox.com/s/v2cts8ximhk3355/DataExport.DataDictionary.February%202022.xlsx?dl=0',
    },
    { name: 'FAQs' },
    { name: 'Submit Bug', href: 'https://forms.gle/8UdF6uYJCyTmwaK19' },
  ];
  return (
    <Drawer
      className={isOpen ? classes.drawer + ' drawerParent' : classes.drawerClosed + ' drawerParent'}
      variant="permanent"
      classes={{
        paper: isOpen ? classes.drawerPaper : classes.drawerClosed,
      }}
    >
      <Box className={classes.topBox}>
        <Logo variant={'admin'} />
      </Box>
      <Typography className={classes.partnerName}>API / Integration Docs</Typography>
      <div
        className={classes.toolbar}
        style={{ display: 'none' }}
      />

      {page_access.map((page_obj, index) => {
        const { name, href } = page_obj;
        return (
          <>
            <List
              className={classes.sideNavList}
              key={`${toUnderScoreSlug(name)}_${index}`}
            >
              <ListItem
                selected={active === name ? true : false}
                button
                key={index}
                className={cn(classes.listItem, active === name ? classes.listItemActive : '')}
              >
                <a
                  href={href ?? '#' + toUnderScoreSlug(name)}
                  target={href ? '_blank' : '_self'}
                  rel="noreferrer"
                >
                  <Tooltip
                    placement="bottom"
                    title={name}
                    aria-label={name}
                  >
                    <ListItemIcon className={classes.ListItemIcon}>
                      {(() => {
                        switch (toUnderScoreSlug(name)) {
                          default:
                            return <Label />;
                          case 'unused':
                            return <MenuBookOutlined />;
                        }
                      })()}
                    </ListItemIcon>
                  </Tooltip>
                  <ListItemText
                    className={classes.listItemText}
                    primary={name}
                  />
                </a>
              </ListItem>
            </List>
            <Divider />
          </>
        );
      })}

      <Divider />
      {userRole && (
        <List className={'SideNavList'}>
          <ListItem
            button
            key="LogOut"
            className={classes.listItem}
          >
            <a
              target="#"
              rel="noopener noreferrer"
              onClick={logout}
            >
              <Tooltip
                placement="right"
                title={'Log Out'}
                aria-label={'Log Out'}
              >
                <ListItemIcon className={classes.ListItemIcon}>
                  <ExitToAppIcon />
                </ListItemIcon>
              </Tooltip>
              <ListItemText
                className={classes.listItemText}
                primary="Log Out"
              />
            </a>
          </ListItem>
        </List>
      )}
      <div
        className={'bottom'}
        style={{
          margin: 'auto 0 20px auto',
          textAlign: 'left',
          width: '100%',
          justifyContent: 'space-between',
          display: 'none',
        }}
      >
        <Typography className={classes.internalInfo}>Build: v{packageJson.version}</Typography>
        {userRole && (
          <Typography className={classes.internalInfo}>Role: {toCapitalizeFromUnderScore(userRole)}</Typography>
        )}
      </div>
    </Drawer>
  );
};

export default APISideBarNavigation;
